import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import NotificationToggles from '../NotificationToggles';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatDate, formatRelative } from './format';
import {
  Badge, DataTable, Drawer, ListLayout, Pagination, Panel, SearchInput, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass, selectClass
} from './AdminUI';

const PAGE_SIZE = 25;
const ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'council', label: 'Council' },
  { value: 'manager', label: 'Manager' }
];
const DEFAULT_PREFERENCES = { newBulletin: true, newForumPost: false, newTicket: false, newForumComment: true, forumMention: true };

const unitOf = (u) => u.unitRef?.number ?? null;
const byLastActive = (a, b) => (b.lastActive ? new Date(b.lastActive).getTime() : 0) - (a.lastActive ? new Date(a.lastActive).getTime() : 0);

function CreateResidentForm({ condo, onDone, onCancel }) {
  const api = useAdminApi();
  const [form, setForm] = useState({ email: '', name: '', role: 'tenant' });
  const [units, setUnits] = useState([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [unit, setUnit] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/api/condos/${condo._id}/units`)
      .then((response) => setUnits(response.data.units || []))
      .catch(() => setUnits([]));
  }, [api, condo._id]);

  const matches = units.filter((n) => String(n).includes(unitSearch));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.role !== 'manager' && unit === null) {
      setError('Please select a unit');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/admin/users', {
        ...form,
        unit: form.role !== 'manager' ? String(unit) : undefined,
        condoId: condo._id
      });
      onDone('Resident created. They get an email explaining how to sign in.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to create user'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-muted-foreground">The account is active right away. They sign in with a code sent to their email.</p>
      <div>
        <label htmlFor="new-resident-email" className={labelClass}>Email</label>
        <input id="new-resident-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="you@example.com" />
      </div>
      <div>
        <label htmlFor="new-resident-name" className={labelClass}>Full name</label>
        <input id="new-resident-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="John Doe" />
      </div>
      <div>
        <label htmlFor="new-resident-role" className={labelClass}>Role</label>
        <select
          id="new-resident-role"
          value={form.role}
          onChange={(e) => { setForm({ ...form, role: e.target.value }); if (e.target.value === 'manager') setUnit(null); }}
          className={inputClass}
        >
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      {form.role !== 'manager' && (
        <div className="relative">
          <label htmlFor="new-resident-unit" className={labelClass}>Unit</label>
          {unit !== null ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded text-sm">
              <span className="flex-1">Unit {unit}</span>
              <button type="button" aria-label="Clear unit" onClick={() => setUnit(null)} className="text-muted-foreground hover:text-destructive">&times;</button>
            </div>
          ) : (
            <>
              <input id="new-resident-unit" value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} className={inputClass} placeholder="Search unit number..." />
              {unitSearch && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {matches.length > 0 ? matches.map((n) => (
                    <button key={n} type="button" onClick={() => { setUnit(n); setUnitSearch(''); }} className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50">
                      Unit {n}
                    </button>
                  )) : <div className="px-3 py-2 text-sm text-muted-foreground">No matching units</div>}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Creating...' : 'Create resident'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

function ResidentDrawer({ resident, onClose, onChanged, notify }) {
  const api = useAdminApi();
  const { user } = useAuth();
  const [form, setForm] = useState(() => ({
    name: resident.name,
    email: resident.email,
    role: resident.role,
    unit: unitOf(resident)?.toString() || '',
    isActive: resident.isActive,
    admin: !!resident.admin,
    notificationPreferences: resident.notificationPreferences || DEFAULT_PREFERENCES
  }));
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/api/admin/users/${resident._id}`, { ...form, unit: form.role !== 'manager' ? form.unit : undefined });
      notify('Resident saved.');
      onChanged();
    } catch (err) {
      alert(errorMessage(err, 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${resident.name}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/admin/users/${resident._id}`);
      notify('Resident deleted.');
      onClose();
      onChanged();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete user'));
    }
  };

  return (
    <Drawer
      title={resident.name}
      badges={<><Badge>{resident.role}</Badge>{resident.admin && <Badge className="text-warning border-warning">Admin</Badge>}</>}
      subtitle={<>Joined {formatDate(resident.createdAt)} · Last active {formatRelative(resident.lastActive)}</>}
      onClose={onClose}
    >
      <form onSubmit={save} className="space-y-3">
        <div>
          <label htmlFor="resident-name" className={labelClass}>Name</label>
          <input id="resident-name" required value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="resident-email" className={labelClass}>Email</label>
          <input id="resident-email" type="email" required value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="resident-role" className={labelClass}>Role</label>
            <select id="resident-role" value={form.role || 'owner'} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputClass}>
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {form.role !== 'manager' && (
            <div>
              <label htmlFor="resident-unit" className={labelClass}>Unit</label>
              <input id="resident-unit" required value={form.unit || ''} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputClass} placeholder="e.g. 101" />
            </div>
          )}
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.admin} onChange={(e) => setForm({ ...form, admin: e.target.checked })} /> Admin
          </label>
        </div>
        <div>
          <span className={labelClass}>Email notifications</span>
          <div className="bg-muted/50 p-3 rounded border border-border">
            {form.notificationPreferences && (
              <NotificationToggles
                preferences={form.notificationPreferences}
                onChange={(prefs) => setForm({ ...form, notificationPreferences: prefs })}
                disabled={saving}
              />
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" onClick={remove} disabled={resident._id === user?._id} className={`${btnDanger} ml-auto`}>Delete</button>
        </div>
      </form>
    </Drawer>
  );
}

function ResidentsTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [users, setUsers] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const q = (params.get('q') || '').trim().toLowerCase();
  const role = params.get('role') || '';
  const page = Number(params.get('page')) || 1;
  const selectedId = params.get('user');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/users', { condo: condo._id });
      setUsers(response.data.users || []);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch residents'));
      setUsers([]);
    }
  }, [api, condo._id]);

  useEffect(() => { load(); }, [load]);

  const changed = () => {
    load();
    onCountsChange();
  };

  const pending = useMemo(() => (users || []).filter((u) => !u.isActive), [users]);
  const active = useMemo(() => (users || [])
    .filter((u) => u.isActive)
    .filter((u) => !role || u.role === role)
    .filter((u) => !q || [u.name, u.email, u.role, unitOf(u)].some((v) => v != null && String(v).toLowerCase().includes(q)))
    .sort(byLastActive), [users, q, role]);

  const pages = Math.max(1, Math.ceil(active.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const rows = active.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const selected = (users || []).find((u) => String(u._id) === String(selectedId));

  const approve = async (resident) => {
    setBusyId(resident._id);
    try {
      await api.put(`/api/admin/users/${resident._id}`, { isActive: true });
      notify(`${resident.name} approved.`);
      changed();
    } catch (err) {
      alert(errorMessage(err, 'Failed to approve user'));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (resident) => {
    if (!window.confirm(`Reject and delete the sign-up from "${resident.name}"?`)) return;
    setBusyId(resident._id);
    try {
      await api.del(`/api/admin/users/${resident._id}`);
      notify('Sign-up rejected.');
      changed();
    } catch (err) {
      alert(errorMessage(err, 'Failed to reject user'));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      className: 'font-medium text-foreground',
      render: (u) => <span className="flex items-center gap-2">{u.name}{u.admin && <Badge className="text-warning border-warning">Admin</Badge>}</span>
    },
    { key: 'email', header: 'Email', hideBelow: 'md', className: 'text-muted-foreground' },
    { key: 'unit', header: 'Unit', render: (u) => unitOf(u) ?? '—' },
    { key: 'role', header: 'Role', className: 'capitalize' },
    { key: 'lastActive', header: 'Last active', hideBelow: 'md', className: 'whitespace-nowrap text-muted-foreground', render: (u) => formatRelative(u.lastActive) }
  ];

  const drawer = creating ? (
    <Drawer title="New resident" onClose={() => setParams({ new: null })}>
      <CreateResidentForm condo={condo} onCancel={() => setParams({ new: null })} onDone={(message) => { notify(message); setParams({ new: null }); changed(); }} />
    </Drawer>
  ) : selected ? (
    <ResidentDrawer key={selected._id} resident={selected} notify={notify} onChanged={changed} onClose={() => setParams({ user: null })} />
  ) : null;

  if (!users) return <Spinner />;

  return (
    <ListLayout drawer={drawer}>
      {pending.length > 0 && (
        <Panel className="mb-4 border-warning">
          <h2 className="text-base font-semibold text-foreground mb-3">Waiting for approval ({pending.length})</h2>
          <ul className="divide-y divide-border">
            {pending.map((u) => (
              <li key={u._id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {u.email} · <span className="capitalize">{u.role}</span>{unitOf(u) != null && ` · Unit ${unitOf(u)}`} · signed up {formatRelative(u.createdAt)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => approve(u)} disabled={busyId === u._id} className={btnPrimary}>Approve</button>
                  <button type="button" onClick={() => reject(u)} disabled={busyId === u._id} className={btnDanger}>Reject</button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Residents ({active.length})</h2>
          <select aria-label="Role" value={role} onChange={(e) => setParams({ role: e.target.value, page: null })} className={selectClass}>
            <option value="">Any role</option>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <SearchInput value={params.get('q') || ''} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search by name, email, unit or role..." />
          <button type="button" onClick={() => setParams({ new: '1', user: null })} className={btnPrimary}>+ New resident</button>
        </Toolbar>
        <DataTable
          label="Residents"
          columns={columns}
          rows={rows}
          selectedId={selectedId}
          onRowClick={(u) => setParams({ user: u._id, new: null })}
          emptyMessage={q || role ? 'No residents match your search.' : 'No residents yet.'}
        />
        <Pagination page={currentPage} pages={pages} total={active.length} limit={PAGE_SIZE} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
      </Panel>
    </ListLayout>
  );
}

export default ResidentsTab;
